import type { ServiceStatus } from '@/lib/schemas/db'

/** Mirrors ServiceType in ServicesScreen without importing from the UI layer. */
export type ArchivedServiceType = 'callout' | 'remote' | 'sitdown' | 'job'

/** One row of the server-side `archived_services` view. */
export interface ArchivedServiceRow {
  /** Unique across service types, e.g. 'job:abc123' */
  key: string
  type: ArchivedServiceType
  /** PocketBase record id in the underlying collection */
  recordId: string
  /** Public-facing ref, e.g. 'J5H-Q4TV' */
  publicId: string
  customerId?: string
  customerPublicId?: string
  customerName: string
  summary: string
  /** UTC ISO timestamp: start time for bookings, created for jobs */
  bookedAt: string
  isWholeDay: boolean
  assigneeName?: string
  status: ServiceStatus
  updatedAt: string
}

export const archivedServiceSortKeys = [
  'ref',
  'type',
  'customer',
  'issue',
  'booked',
  'assignee',
  'status',
  'updated',
] as const

export type ArchivedServiceSortKey = (typeof archivedServiceSortKeys)[number]

export interface ArchivedServicesSearchFilters {
  /** Substring match on the public ref */
  ref?: string
  /** Exact match on service type */
  type?: string
  /** Substring match on customer name */
  customer?: string
  /** Exact match on the customer's PocketBase record id */
  customerId?: string
  /** Substring match on the issue/fault summary */
  issue?: string
  /** Substring match on assignee name */
  assignee?: string
  /** Exact match on status */
  status?: string
}

export interface ArchivedServicesSearchInput {
  filters?: ArchivedServicesSearchFilters
  sort?: { key: ArchivedServiceSortKey; dir: 'asc' | 'desc' }
  page: number
  pageSize: number
}

/**
 * Search results come back as ordered keys: the matching rows themselves are
 * upserted into the adapter's archived-rows collection, so the UI reads the
 * records through TanStack DB and uses the keys only for membership + order.
 */
export interface ArchivedServicesSearchResult {
  keys: string[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

/** A materialised page of archived rows, before being upserted + keyed. */
export interface ArchivedServicesSearchPage {
  items: ArchivedServiceRow[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

export function isArchivedStatus(status: string): boolean {
  return status === 'Invoiced' || status === 'Cancelled'
}

const sortValue: Record<ArchivedServiceSortKey, (row: ArchivedServiceRow) => string> = {
  ref: (row) => row.publicId,
  type: (row) => row.type,
  customer: (row) => row.customerName.toLowerCase(),
  issue: (row) => row.summary.toLowerCase(),
  booked: (row) => row.bookedAt,
  assignee: (row) => (row.assigneeName ?? '').toLowerCase(),
  status: (row) => row.status,
  updated: (row) => row.updatedAt,
}

const contains = (value: string, query: string) =>
  value.toLowerCase().includes(query.toLowerCase())

/**
 * In-memory equivalent of the PocketBase `archived_services` search, so the
 * in-memory adapter (Storybook/tests) behaves like the server: same filter
 * semantics (`~` substring, `=` exact), sort keys and pagination shape.
 */
export function searchArchivedServiceRows(
  rows: ArchivedServiceRow[],
  input: ArchivedServicesSearchInput,
): ArchivedServicesSearchPage {
  const { filters = {}, sort } = input

  const filtered = rows.filter((row) => {
    if (filters.ref && !contains(row.publicId, filters.ref)) return false
    if (filters.type && row.type !== filters.type) return false
    if (filters.customer && !contains(row.customerName, filters.customer)) return false
    if (filters.customerId && row.customerId !== filters.customerId) return false
    if (filters.issue && !contains(row.summary, filters.issue)) return false
    if (filters.assignee && !contains(row.assigneeName ?? '', filters.assignee)) return false
    if (filters.status && row.status !== filters.status) return false
    return true
  })

  if (sort) {
    const dir = sort.dir === 'asc' ? 1 : -1
    const value = sortValue[sort.key]
    filtered.sort((a, b) => {
      const av = value(a)
      const bv = value(b)
      return av < bv ? -dir : av > bv ? dir : 0
    })
  }

  const pageSize = Math.max(1, input.pageSize)
  const totalItems = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const page = Math.min(Math.max(1, input.page), totalPages)
  const start = (page - 1) * pageSize

  return {
    items: filtered.slice(start, start + pageSize),
    page,
    pageSize,
    totalItems,
    totalPages,
  }
}
